import { useEffect, useRef } from "react";
import { toast } from "sonner";

/**
 * Poll /version.json and warn the user when the currently loaded SPA is
 * older than the deployed build. Reads the buildId this tab booted with
 * from <meta name="build-id"> (injected by build-canary-plugin).
 *
 * On mismatch:
 *   - Sonner toast with "Reload" action (hard reload, bypassing cache).
 *   - Fires once per detected buildId — no re-nagging until a newer one ships.
 */
export function useVersionMismatchCheck(options?: {
  intervalMs?: number;
  url?: string;
}) {
  const intervalMs = options?.intervalMs ?? 5 * 60 * 1000; // 5 min
  const url = options?.url ?? "/version.json";
  const notifiedFor = useRef<Set<string>>(new Set());

  useEffect(() => {
    const loadedBuildId =
      document
        .querySelector('meta[name="build-id"]')
        ?.getAttribute("content") ?? null;
    // Dev / preview HTML has no build-id meta — nothing meaningful to compare.
    if (!loadedBuildId) return;

    let cancelled = false;

    const hardReload = () => {
      const u = new URL(window.location.href);
      u.searchParams.set("_v", Date.now().toString());
      window.location.replace(u.toString());
    };

    const check = async () => {
      try {
        const res = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return;
        const j = (await res.json()) as { buildId?: string };
        if (cancelled || !j.buildId) return;
        if (j.buildId === loadedBuildId) return;
        if (notifiedFor.current.has(j.buildId)) return;
        notifiedFor.current.add(j.buildId);

        toast("A newer version is available", {
          description: "Your tab is on an older build. Reload to get the latest.",
          duration: Infinity,
          action: { label: "Reload", onClick: hardReload },
        });
      } catch {
        /* offline or blocked — ignore */
      }
    };

    // Initial check on mount, then interval, and on tab refocus / back-online.
    void check();
    const id = window.setInterval(check, intervalMs);
    const onVis = () => document.visibilityState === "visible" && void check();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("online", onVis);

    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("online", onVis);
    };
  }, [intervalMs, url]);
}
